import { Icon, MenuItem, Spinner } from "@blueprintjs/core";
import {
    ItemListRenderer,
    ItemListRendererProps,
    ItemRenderer,
    Omnibar
} from "@blueprintjs/select";
import {
    segmentTrackOpenSearchPalette,
    segmentTrackSearchPaletteAbandon,
    segmentTrackSearchPaletteNavigate
} from "in_queue/analytics";
import { ReactComponent as ClusterIcon } from "in_queue/icons/cluster.svg";
import {
    determineFuelType,
    FuelTypeIcon
} from "in_queue/icons/fuelType/FuelTypeIcon";
import { ReactComponent as ScenarioIcon } from "in_queue/icons/scenario.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchCategory, SearchItem } from "./searchItems";
import css from "./SearchPalette.module.scss";
import { useSearchPalette } from "./SearchPaletteContext";
import { useSearchPaletteItems } from "./useSearchPaletteItems";

const IS_MAC = /Mac/.test(navigator.userAgent);
const KEYBOARD_SHORTCUT = IS_MAC ? "\u2318K" : "Ctrl+K";

const CATEGORY_ICONS: Record<SearchCategory, JSX.Element> = {
    [SearchCategory.CLUSTER]: <ClusterIcon width={16} height={16} />,
    [SearchCategory.PROJECT]: <Icon icon="generate" />,
    [SearchCategory.SCENARIO]: <ScenarioIcon width={16} height={16} />
};

const CATEGORY_ORDER: ReadonlyArray<SearchCategory> = [
    SearchCategory.CLUSTER,
    SearchCategory.PROJECT,
    SearchCategory.SCENARIO
];

const CATEGORY_LABELS: Record<SearchCategory, string> = {
    [SearchCategory.CLUSTER]: "Clusters",
    [SearchCategory.PROJECT]: "Projects",
    [SearchCategory.SCENARIO]: "Scenarios"
};

const getMaxResults = (): number => {
    // We want to limit the number of results shown in the search to avoid overwhelming
    // the user and to ensure that the search remains usable on smaller screens

    // We leave 20% of the viewport height as margin above and below the search
    const availableViewport = window.innerHeight * 0.6;

    // We estimate that the search itself (input, padding, footer, category headers)
    // takes up around 200px of vertical space, so we subtract that from the available
    // viewport height to determine how many results we can show without scrolling
    const available = availableViewport - 200;

    const rowHeight = 30; // This is an estimate of the height of each result row, including padding
    const minResults = 3; // We want to show at least 3 results even on very small screens
    return Math.max(minResults, Math.floor(available / rowHeight));
};

const limitSearchItems = (items: SearchItem[]): SearchItem[] => {
    // We limit the number of results per category in a way that tries to balance the categories
    // while respecting the overall limit. We first give each category an equal share of the total
    // limit, then distribute any remaining slots to categories that have more items until we reach
    // the total limit. This way we ensure that if there are many items in one category and few in
    // others, we still show a good representation of all categories without exceeding the total limit.

    // Group items by category
    const categoryToItems: Partial<Record<SearchCategory, SearchItem[]>> = {};
    items.forEach((item) => {
        const categoryItems = categoryToItems[item.category];
        if (categoryItems) {
            categoryItems.push(item);
        } else {
            categoryToItems[item.category] = [item];
        }
    });

    // Only consider categories that have at least one item
    const categories = CATEGORY_ORDER.filter(
        (category) => categoryToItems[category]
    );

    // First pass: give each category an equal share of the total items.
    // If a category has fewer items than its share, the surplus carries
    // forward so later categories can use more.
    const limits: Partial<Record<SearchCategory, number>> = {};
    let remaining = getMaxResults();

    categories.forEach((category, index) => {
        const categoriesLeft = categories.length - index;
        const perCategory = Math.floor(remaining / categoriesLeft);
        const available = categoryToItems[category]?.length ?? 0;
        const limit = Math.min(perCategory, available);
        limits[category] = limit;
        remaining -= limit;
    });

    // Second pass: distribute any leftover slots to earlier categories
    // that had more items than their initial share allowed.
    categories.forEach((cat) => {
        const limit = limits[cat] ?? 0;
        const available = categoryToItems[cat]?.length ?? 0;
        const extra = Math.min(remaining, available - limit);
        if (extra > 0) {
            limits[cat] = limit + extra;
            remaining -= extra;
        }
    });

    // Collect results in category order, respecting each category's limit
    const result: Array<SearchItem> = [];
    categories.forEach((cat) => {
        const group = categoryToItems[cat] ?? [];
        result.push(...group.slice(0, limits[cat]));
    });

    return result;
};

const itemListPredicate = (
    query: string,
    items: Array<SearchItem>
): Array<SearchItem> => {
    // We split the query into words and check that each word is included in the searchText.This allows
    // for more flexible matching (e.g. "scenario base" will match with any scenario that has both "scenario"
    // and "base" in its searchText, even if they are not adjacent)
    const queryWords = query.toLowerCase().split(/\s+/).filter(Boolean);

    const isEmptyQuery = !query.trim();
    const filtered = isEmptyQuery
        ? items
        : items.filter((item) =>
              queryWords.every((word) => item.searchText.includes(word))
          );

    return limitSearchItems(filtered);
};

const renderItem: ItemRenderer<SearchItem> = (
    item,
    { handleClick, handleFocus, modifiers }
) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }

    const labelElement = (
        <span className={css["SearchPalette-secondary-label"]}>
            {item.secondaryLabel}
        </span>
    );

    const icon =
        item.category === SearchCategory.PROJECT &&
        determineFuelType(item.fuelType) ? (
            <FuelTypeIcon fuelType={item.fuelType} />
        ) : (
            CATEGORY_ICONS[item.category]
        );

    return (
        <MenuItem
            className={css["SearchPalette-item"]}
            active={modifiers.active}
            key={item.id}
            onClick={handleClick}
            onFocus={handleFocus}
            icon={icon}
            text={item.label}
            labelElement={labelElement}
        />
    );
};

export const SearchPalette: React.FC = () => {
    const { isOpen, close, toggle } = useSearchPalette();
    const [query, setQuery] = useState("");

    // Ref lets handleClose and handleItemSelect read the latest query
    // without adding it as a dependency, which would recreate them on every keystroke
    const queryRef = useRef(query);
    queryRef.current = query;

    const items = useSearchPaletteItems();
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) {
            segmentTrackOpenSearchPalette();
        }
    }, [isOpen]);

    const handleClose = useCallback(
        (didNavigate: boolean) => {
            if (!didNavigate && queryRef.current.trim().length > 0) {
                segmentTrackSearchPaletteAbandon({
                    query: queryRef.current
                });
            }
            close();
            setQuery("");
        },
        [close]
    );

    useEffect(() => {
        // The search can be toggled by pressing Ctrl+K (or Cmd+K on Mac). This is a common keyboard
        // shortcut for search in many applications
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                toggle();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [toggle]);

    const handleItemSelect = useCallback(
        (item: SearchItem) => {
            segmentTrackSearchPaletteNavigate({
                query: queryRef.current,
                category: item.category,
                label: item.label
            });
            handleClose(true);
            navigate(item.navigateTo);
        },
        [handleClose, navigate]
    );

    const renderItemList: ItemListRenderer<SearchItem> = useCallback(
        ({ filteredItems, renderItem }: ItemListRendererProps<SearchItem>) => {
            const groupedItems: Partial<
                Record<SearchCategory, ReadonlyArray<React.ReactNode>>
            > = {};

            filteredItems.forEach((item, index) => {
                const rendered = renderItem(item, index);
                if (rendered) {
                    const existing = groupedItems[item.category];
                    groupedItems[item.category] = existing
                        ? [...existing, rendered]
                        : [rendered];
                }
            });

            const hasResults = Object.keys(groupedItems).length > 0;

            return (
                <ul className={css["SearchPalette-results"]}>
                    {hasResults ? (
                        CATEGORY_ORDER.filter((cat) => groupedItems[cat]).map(
                            (category) => (
                                <div key={category}>
                                    <div
                                        className={
                                            css["SearchPalette-category-header"]
                                        }
                                    >
                                        {CATEGORY_LABELS[category]}
                                    </div>
                                    {groupedItems[category]}
                                </div>
                            )
                        )
                    ) : (
                        <MenuItem
                            disabled
                            text="No results found"
                            className={css["SearchPalette-no-results"]}
                        />
                    )}
                    <div className={css["SearchPalette-footer"]}>
                        <span className={css["SearchPalette-kbd"]}>
                            {KEYBOARD_SHORTCUT}
                        </span>
                        <span>to toggle search</span>
                    </div>
                </ul>
            );
        },
        []
    );

    const onClose = useCallback(() => handleClose(false), [handleClose]);

    if (items === "loading") {
        return (
            <Omnibar<SearchItem>
                className={css["SearchPalette"]}
                isOpen={isOpen}
                onClose={onClose}
                items={[]}
                itemRenderer={renderItem}
                itemListRenderer={() => (
                    <div className={css["SearchPalette-loading"]}>
                        <Spinner size={24} />
                    </div>
                )}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onItemSelect={() => {}}
            />
        );
    }

    return (
        <Omnibar<SearchItem>
            className={css["SearchPalette"]}
            isOpen={isOpen}
            onClose={onClose}
            query={query}
            onQueryChange={setQuery}
            items={items}
            itemListPredicate={itemListPredicate}
            itemRenderer={renderItem}
            itemListRenderer={renderItemList}
            onItemSelect={handleItemSelect}
        />
    );
};
